import axios, { AxiosResponse } from 'axios';
import fileDownload from 'js-file-download';
import { TJotformWarning } from 'redux/reducers/jotform/types';
import { call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import { api } from 'services/api';
import { PAGE_SIZE } from 'services/constants/env';
import { ESortDirection } from 'ui/EasTable/types';
import { notificationContainer } from 'utils/notificationContainer';
import {
	getPpwrLastEmSettingsParams,
	getPpwrLastFeesParams,
	getPpwrLastJotformsParams,
	getPpwrLastEmPackagingTypesParams,
	getPpwrLastPackagingTypesParams,
	getPpwrLastEmCountryExclusionsParams,
} from './selectors';
import {
	createPpwrFeeRequest,
	getPpwrFeeFailure,
	getPpwrFeeRequest,
	getPpwrFeeSuccess,
	savePpwrFeeFailure,
	savePpwrFeeSuccess,
	searchPpwrFeesFailure,
	searchPpwrFeesRequest,
	searchPpwrFeesSuccess,
	updatePpwrFeeRequest,
	createPpwrPackagingTypeRequest,
	getPpwrPackagingTypeFailure,
	getPpwrPackagingTypeOptionsFailure,
	getPpwrPackagingTypeOptionsRequest,
	getPpwrPackagingTypeOptionsSuccess,
	getPpwrPackagingTypeRequest,
	getPpwrPackagingTypeSuccess,
	savePpwrPackagingTypeFailure,
	savePpwrPackagingTypeSuccess,
	searchPpwrPackagingTypesFailure,
	searchPpwrPackagingTypesRequest,
	searchPpwrPackagingTypesSuccess,
	updatePpwrPackagingTypeHandlerRequest,
	updatePpwrPackagingTypeRequest,
	getPpwrEmSettingsFailure,
	getPpwrEmSettingsRequest,
	getPpwrEmSettingsSuccess,
	savePpwrEmSettingsFailure,
	savePpwrEmSettingsSuccess,
	searchPpwrEmSettingsFailure,
	searchPpwrEmSettingsRequest,
	searchPpwrEmSettingsSuccess,
	updatePpwrEmSettingsRequest,
	activatePpwrServiceFailure,
	activatePpwrServiceRequest,
	activatePpwrServiceSuccess,
	activatePpwrServiceWarning,
	getPpwrJotformFailure,
	getPpwrJotformRequest,
	getPpwrJotformSuccess,
	searchPpwrJotformsFailure,
	searchPpwrJotformsRequest,
	searchPpwrJotformsSuccess,
	getPpwrFeeOptionsFailure,
	getPpwrFeeOptionsRequest,
	getPpwrFeeOptionsSuccess,
	deletePpwrEmPackagingTypeDocumentFinish,
	deletePpwrEmPackagingTypeDocumentRequest,
	downloadPpwrEmPackagingTypeDocumentFinish,
	downloadPpwrEmPackagingTypeDocumentRequest,
	searchPpwrEmPackagingTypesFailure,
	searchPpwrEmPackagingTypesRequest,
	searchPpwrEmPackagingTypesSuccess,
	updatePpwrEmPackagingTypeFailure,
	updatePpwrEmPackagingTypeRequest,
	updatePpwrEmPackagingTypeSuccess,
	uploadPpwrEmPackagingTypeDocumentFinish,
	uploadPpwrEmPackagingTypeDocumentRequest,
	createPpwrEmCountryExclusionRequest,
	savePpwrEmCountryExclusionFailure,
	savePpwrEmCountryExclusionSuccess,
	searchPpwrEmCountryExclusionsFailure,
	searchPpwrEmCountryExclusionsRequest,
	searchPpwrEmCountryExclusionsSuccess,
	updatePpwrEmCountryExclusionRequest,
} from './reducer';
import {
	TCreateOrUpdatePpwrFeeRequest,
	TPpwrFee,
	TPpwrFeeIdRequest,
	TPpwrFeeListResponse,
	TPpwrFeeSearchRequest,
	TUpdatePpwrFeeRequest,
	TCreateOrUpdatePpwrPackagingTypeRequest,
	TPpwrPackagingType,
	TPpwrPackagingTypeIdRequest,
	TPpwrPackagingTypeListResponse,
	TPpwrPackagingTypeSearchRequest,
	TUpdatePpwrPackagingTypeHandlerRequest,
	TUpdatePpwrPackagingTypeRequest,
	TPpwrEmSettings,
	TPpwrEmSettingsIdRequest,
	TPpwrEmSettingsListResponse,
	TPpwrEmSettingsSearchRequest,
	TUpdatePpwrEmSettingsRequest,
	TActivatePpwrServiceRequest,
	TPpwrJotform,
	TPpwrJotformIdRequest,
	TPpwrJotformListResponse,
	TPpwrJotformSearchRequest,
	TDownloadPpwrEmPackagingTypeDocumentRequest,
	TPpwrEmPackagingTypeDocumentRequest,
	TPpwrEmPackagingTypeListResponse,
	TPpwrEmPackagingTypeSearchRequest,
	TUpdatePpwrEmPackagingTypeRequest,
	TUploadPpwrEmPackagingTypeDocumentRequest,
	TCreateOrUpdatePpwrEmCountryExclusionRequest,
	TPpwrEmCountryExclusionListResponse,
	TPpwrEmCountryExclusionSearchRequest,
	TUpdatePpwrEmCountryExclusionRequest,
} from './types';

const DEFAULT_LIST_PARAMS: TPpwrPackagingTypeSearchRequest = {
	pageSize: PAGE_SIZE,
	pageNumber: 1,
};

/** Packaging types are a short dictionary, so one large page holds all of them. */
const OPTIONS_PARAMS: TPpwrPackagingTypeSearchRequest = {
	pageSize: 1000,
	pageNumber: 1,
	orderBy: [{ fieldName: 'pckgTypeName', direction: ESortDirection.ASC }],
};

/** Shows the message the API returned, falling back to the passed default. */
function notifyError(error: unknown, fallback: string) {
	if (axios.isAxiosError(error)) {
		notificationContainer(
			String(error?.response?.data?.message ?? error.message ?? fallback),
			'error',
		);
		return;
	}
	notificationContainer(fallback, 'error');
}

function* searchPackagingTypesWorker({ payload }: PayloadAction<TPpwrPackagingTypeSearchRequest>) {
	try {
		const response: TPpwrPackagingTypeListResponse = yield call(
			api.ppwr.searchPackagingTypes,
			payload,
		);
		yield put(searchPpwrPackagingTypesSuccess(response));
	} catch (error) {
		yield put(searchPpwrPackagingTypesFailure());
		notifyError(error, 'Failed to load packaging types');
	}
}

function* getPackagingTypeOptionsWorker() {
	try {
		const response: TPpwrPackagingTypeListResponse = yield call(
			api.ppwr.searchPackagingTypes,
			OPTIONS_PARAMS,
		);
		yield put(getPpwrPackagingTypeOptionsSuccess(response.data ?? []));
	} catch (error) {
		yield put(getPpwrPackagingTypeOptionsFailure());
		notifyError(error, 'Failed to load packaging types');
	}
}

/** Reloads the table with its last params and the select options after a save. */
function* refetchPackagingTypes() {
	const lastParams: TPpwrPackagingTypeSearchRequest | null = yield select(
		getPpwrLastPackagingTypesParams,
	);
	const response: TPpwrPackagingTypeListResponse = yield call(
		api.ppwr.searchPackagingTypes,
		lastParams ?? DEFAULT_LIST_PARAMS,
	);
	yield put(searchPpwrPackagingTypesSuccess(response));
	yield put(getPpwrPackagingTypeOptionsRequest());
}

function* getPackagingTypeWorker({ payload }: PayloadAction<TPpwrPackagingTypeIdRequest>) {
	try {
		const response: TPpwrPackagingType = yield call(api.ppwr.getPackagingType, payload);
		yield put(getPpwrPackagingTypeSuccess(response));
	} catch (error) {
		yield put(getPpwrPackagingTypeFailure());
		notifyError(error, 'Failed to load packaging type');
	}
}

function* createPackagingTypeWorker({
	payload,
}: PayloadAction<TCreateOrUpdatePpwrPackagingTypeRequest>) {
	try {
		yield call(api.ppwr.createPackagingType, payload);
		yield call(refetchPackagingTypes);
		yield put(savePpwrPackagingTypeSuccess());
		notificationContainer('Packaging type was successfully created', 'success');
	} catch (error) {
		yield put(savePpwrPackagingTypeFailure());
		notifyError(error, 'Failed to create packaging type');
	}
}

function* updatePackagingTypeWorker({ payload }: PayloadAction<TUpdatePpwrPackagingTypeRequest>) {
	try {
		let response: TPpwrPackagingType = yield call(api.ppwr.updatePackagingType, payload);
		if (payload.handler) {
			response = yield call(api.ppwr.updatePackagingTypeHandler, {
				id: payload.id,
				body: payload.handler,
			});
		}
		yield put(getPpwrPackagingTypeSuccess(response));
		yield call(refetchPackagingTypes);
		yield put(savePpwrPackagingTypeSuccess());
		notificationContainer('Packaging type was successfully updated', 'success');
	} catch (error) {
		yield put(savePpwrPackagingTypeFailure());
		notifyError(error, 'Failed to update packaging type');
	}
}

function* updatePackagingTypeHandlerWorker({
	payload,
}: PayloadAction<TUpdatePpwrPackagingTypeHandlerRequest>) {
	try {
		const response: TPpwrPackagingType = yield call(api.ppwr.updatePackagingTypeHandler, payload);
		yield put(getPpwrPackagingTypeSuccess(response));
		yield call(refetchPackagingTypes);
		yield put(savePpwrPackagingTypeSuccess());
		notificationContainer('Packaging type handler was successfully updated', 'success');
	} catch (error) {
		yield put(savePpwrPackagingTypeFailure());
		notifyError(error, 'Failed to update packaging type handler');
	}
}

function* searchFeesWorker({ payload }: PayloadAction<TPpwrFeeSearchRequest>) {
	try {
		const response: TPpwrFeeListResponse = yield call(api.ppwr.searchFees, payload);
		yield put(searchPpwrFeesSuccess(response));
	} catch (error) {
		yield put(searchPpwrFeesFailure());
		notifyError(error, 'Failed to load EPR service fees');
	}
}

/** Reloads the fees table with its last params after a save. */
function* refetchFees() {
	const lastParams: TPpwrFeeSearchRequest | null = yield select(getPpwrLastFeesParams);
	const response: TPpwrFeeListResponse = yield call(
		api.ppwr.searchFees,
		lastParams ?? DEFAULT_LIST_PARAMS,
	);
	yield put(searchPpwrFeesSuccess(response));
}

function* getFeeWorker({ payload }: PayloadAction<TPpwrFeeIdRequest>) {
	try {
		const response: TPpwrFee = yield call(api.ppwr.getFee, payload);
		yield put(getPpwrFeeSuccess(response));
	} catch (error) {
		yield put(getPpwrFeeFailure());
		notifyError(error, 'Failed to load EPR service fee');
	}
}

function* createFeeWorker({ payload }: PayloadAction<TCreateOrUpdatePpwrFeeRequest>) {
	try {
		yield call(api.ppwr.createFee, payload);
		yield call(refetchFees);
		yield put(savePpwrFeeSuccess());
		notificationContainer('EPR service fee was successfully created', 'success');
	} catch (error) {
		yield put(savePpwrFeeFailure());
		notifyError(error, 'Failed to create EPR service fee');
	}
}

function* updateFeeWorker({ payload }: PayloadAction<TUpdatePpwrFeeRequest>) {
	try {
		const response: TPpwrFee = yield call(api.ppwr.updateFee, payload);
		yield put(getPpwrFeeSuccess(response));
		yield call(refetchFees);
		yield put(savePpwrFeeSuccess());
		notificationContainer('EPR service fee was successfully updated', 'success');
	} catch (error) {
		yield put(savePpwrFeeFailure());
		notifyError(error, 'Failed to update EPR service fee');
	}
}

function* searchEmSettingsWorker({ payload }: PayloadAction<TPpwrEmSettingsSearchRequest>) {
	try {
		const response: TPpwrEmSettingsListResponse = yield call(api.ppwr.searchEmSettings, payload);
		yield put(searchPpwrEmSettingsSuccess(response));
	} catch (error) {
		yield put(searchPpwrEmSettingsFailure());
		notifyError(error, 'Failed to load EM PPWR settings');
	}
}

/** Reloads the EM settings table with its last params after a save, if it was loaded. */
function* refetchEmSettings() {
	const lastParams: TPpwrEmSettingsSearchRequest | null = yield select(getPpwrLastEmSettingsParams);
	if (!lastParams) return;
	const response: TPpwrEmSettingsListResponse = yield call(api.ppwr.searchEmSettings, lastParams);
	yield put(searchPpwrEmSettingsSuccess(response));
}

function* getEmSettingsWorker({ payload }: PayloadAction<TPpwrEmSettingsIdRequest>) {
	try {
		const response: TPpwrEmSettings = yield call(api.ppwr.getEmSettings, payload);
		yield put(getPpwrEmSettingsSuccess(response));
	} catch (error) {
		yield put(getPpwrEmSettingsFailure());
		notifyError(error, 'Failed to load EM PPWR settings');
	}
}

function* updateEmSettingsWorker({ payload }: PayloadAction<TUpdatePpwrEmSettingsRequest>) {
	try {
		const response: TPpwrEmSettings = yield call(api.ppwr.updateEmSettings, payload);
		yield put(getPpwrEmSettingsSuccess(response));
		yield call(refetchEmSettings);
		yield put(savePpwrEmSettingsSuccess());
		notificationContainer('EM PPWR settings were successfully updated', 'success');
	} catch (error) {
		yield put(savePpwrEmSettingsFailure());
		notifyError(error, 'Failed to update EM PPWR settings');
	}
}

function* searchJotformsWorker({ payload }: PayloadAction<TPpwrJotformSearchRequest>) {
	try {
		const response: TPpwrJotformListResponse = yield call(api.ppwr.searchJotforms, payload);
		yield put(searchPpwrJotformsSuccess(response));
	} catch (error) {
		yield put(searchPpwrJotformsFailure());
		notifyError(error, 'Failed to load PPWR jotforms');
	}
}

/** Reloads the jotforms table with its last params after an activation, if it was loaded. */
function* refetchJotforms() {
	const lastParams: TPpwrJotformSearchRequest | null = yield select(getPpwrLastJotformsParams);
	if (!lastParams) return;
	const response: TPpwrJotformListResponse = yield call(api.ppwr.searchJotforms, lastParams);
	yield put(searchPpwrJotformsSuccess(response));
}

function* getJotformWorker({ payload }: PayloadAction<TPpwrJotformIdRequest>) {
	try {
		const response: TPpwrJotform = yield call(api.ppwr.getJotform, payload);
		yield put(getPpwrJotformSuccess(response));
	} catch (error) {
		yield put(getPpwrJotformFailure());
		notifyError(error, 'Failed to load PPWR jotform');
	}
}

function* activateServiceWorker({ payload }: PayloadAction<TActivatePpwrServiceRequest>) {
	try {
		const response: AxiosResponse<TJotformWarning | null> = yield call(
			api.ppwr.activateService,
			payload,
		);
		if (response.status === 210 && response.data) {
			yield put(activatePpwrServiceWarning(response.data));
			notificationContainer(response.data.message ?? 'Merchant may already exist', 'warning');
			return;
		}
		yield put(activatePpwrServiceSuccess());
		notificationContainer('PPWR service was successfully activated', 'success');
		yield put(getPpwrJotformRequest({ jotformId: payload.jotformId }));
		yield call(refetchJotforms);
		yield call(refetchEmSettings);
	} catch (error) {
		yield put(activatePpwrServiceFailure());
		notifyError(error, 'Failed to activate PPWR service');
	}
}

/** EPR service fees are a short dictionary, so one large page holds all of them. */
const FEE_OPTIONS_PARAMS: TPpwrFeeSearchRequest = {
	pageSize: 1000,
	pageNumber: 1,
	orderBy: [{ fieldName: 'planName', direction: ESortDirection.ASC }],
};

function* getFeeOptionsWorker() {
	try {
		const response: TPpwrFeeListResponse = yield call(api.ppwr.searchFees, FEE_OPTIONS_PARAMS);
		yield put(getPpwrFeeOptionsSuccess(response.data ?? []));
	} catch (error) {
		yield put(getPpwrFeeOptionsFailure());
		notifyError(error, 'Failed to load EPR service fees');
	}
}

function* searchEmPackagingTypesWorker({
	payload,
}: PayloadAction<TPpwrEmPackagingTypeSearchRequest>) {
	try {
		const response: TPpwrEmPackagingTypeListResponse = yield call(
			api.ppwr.searchEmPackagingTypes,
			payload,
		);
		yield put(searchPpwrEmPackagingTypesSuccess(response));
	} catch (error) {
		yield put(searchPpwrEmPackagingTypesFailure());
		notifyError(error, 'Failed to load EM packaging types');
	}
}

/** Reloads the EM packaging types with their last params, if they were loaded for this EM. */
function* refetchEmPackagingTypes(emId: number) {
	const lastParams: TPpwrEmPackagingTypeSearchRequest | null = yield select(
		getPpwrLastEmPackagingTypesParams,
	);
	if (!lastParams || lastParams.emId !== emId) return;
	const response: TPpwrEmPackagingTypeListResponse = yield call(
		api.ppwr.searchEmPackagingTypes,
		lastParams,
	);
	yield put(searchPpwrEmPackagingTypesSuccess(response));
}

function* updateEmPackagingTypeWorker({
	payload,
}: PayloadAction<TUpdatePpwrEmPackagingTypeRequest>) {
	try {
		yield call(api.ppwr.updateEmPackagingType, payload);
		yield call(refetchEmPackagingTypes, payload.emId);
		yield put(updatePpwrEmPackagingTypeSuccess());
		notificationContainer('Packaging type was successfully updated', 'success');
	} catch (error) {
		yield put(updatePpwrEmPackagingTypeFailure());
		notifyError(error, 'Failed to update packaging type');
	}
}

function* uploadEmPackagingTypeDocumentWorker({
	payload,
}: PayloadAction<TUploadPpwrEmPackagingTypeDocumentRequest>) {
	try {
		yield call(api.ppwr.uploadEmPackagingTypeDocument, payload);
		yield call(refetchEmPackagingTypes, payload.emId);
		notificationContainer('Document was successfully uploaded', 'success');
	} catch (error) {
		notifyError(error, 'Failed to upload document');
	} finally {
		yield put(uploadPpwrEmPackagingTypeDocumentFinish());
	}
}

function* downloadEmPackagingTypeDocumentWorker({
	payload,
}: PayloadAction<TDownloadPpwrEmPackagingTypeDocumentRequest>) {
	try {
		const blob: Blob = yield call(api.ppwr.downloadEmPackagingTypeDocument, payload);
		if (blob) fileDownload(blob, payload.fileName);
	} catch (error) {
		notifyError(error, 'Failed to download document');
	} finally {
		yield put(downloadPpwrEmPackagingTypeDocumentFinish());
	}
}

function* deleteEmPackagingTypeDocumentWorker({
	payload,
}: PayloadAction<TPpwrEmPackagingTypeDocumentRequest>) {
	try {
		yield call(api.ppwr.deleteEmPackagingTypeDocument, payload);
		yield call(refetchEmPackagingTypes, payload.emId);
		notificationContainer('Document was successfully deleted', 'success');
	} catch (error) {
		notifyError(error, 'Failed to delete document');
	} finally {
		yield put(deletePpwrEmPackagingTypeDocumentFinish());
	}
}

function* searchEmCountryExclusionsWorker({
	payload,
}: PayloadAction<TPpwrEmCountryExclusionSearchRequest>) {
	try {
		const response: TPpwrEmCountryExclusionListResponse = yield call(
			api.ppwr.searchEmCountryExclusions,
			payload,
		);
		yield put(searchPpwrEmCountryExclusionsSuccess(response));
	} catch (error) {
		yield put(searchPpwrEmCountryExclusionsFailure());
		notifyError(error, 'Failed to load country exclusions');
	}
}

/** Reloads the country exclusions with their last params after a save, if they were loaded. */
function* refetchEmCountryExclusions() {
	const lastParams: TPpwrEmCountryExclusionSearchRequest | null = yield select(
		getPpwrLastEmCountryExclusionsParams,
	);
	if (!lastParams) return;
	const response: TPpwrEmCountryExclusionListResponse = yield call(
		api.ppwr.searchEmCountryExclusions,
		lastParams,
	);
	yield put(searchPpwrEmCountryExclusionsSuccess(response));
}

function* createEmCountryExclusionWorker({
	payload,
}: PayloadAction<TCreateOrUpdatePpwrEmCountryExclusionRequest>) {
	try {
		yield call(api.ppwr.createEmCountryExclusion, payload);
		yield call(refetchEmCountryExclusions);
		yield put(savePpwrEmCountryExclusionSuccess());
		notificationContainer('Country exclusion was successfully created', 'success');
	} catch (error) {
		yield put(savePpwrEmCountryExclusionFailure());
		notifyError(error, 'Failed to create country exclusion');
	}
}

function* updateEmCountryExclusionWorker({
	payload,
}: PayloadAction<TUpdatePpwrEmCountryExclusionRequest>) {
	try {
		yield call(api.ppwr.updateEmCountryExclusion, payload);
		yield call(refetchEmCountryExclusions);
		yield put(savePpwrEmCountryExclusionSuccess());
		notificationContainer('Country exclusion was successfully updated', 'success');
	} catch (error) {
		yield put(savePpwrEmCountryExclusionFailure());
		notifyError(error, 'Failed to update country exclusion');
	}
}

export function* ppwrSaga() {
	yield takeLatest(searchPpwrPackagingTypesRequest.type, searchPackagingTypesWorker);
	yield takeLatest(getPpwrPackagingTypeOptionsRequest.type, getPackagingTypeOptionsWorker);
	yield takeLatest(getPpwrPackagingTypeRequest.type, getPackagingTypeWorker);
	yield takeLatest(createPpwrPackagingTypeRequest.type, createPackagingTypeWorker);
	yield takeLatest(updatePpwrPackagingTypeRequest.type, updatePackagingTypeWorker);
	yield takeLatest(updatePpwrPackagingTypeHandlerRequest.type, updatePackagingTypeHandlerWorker);
	yield takeLatest(searchPpwrFeesRequest.type, searchFeesWorker);
	yield takeLatest(getPpwrFeeRequest.type, getFeeWorker);
	yield takeLatest(createPpwrFeeRequest.type, createFeeWorker);
	yield takeLatest(updatePpwrFeeRequest.type, updateFeeWorker);
	yield takeLatest(searchPpwrEmSettingsRequest.type, searchEmSettingsWorker);
	yield takeLatest(getPpwrEmSettingsRequest.type, getEmSettingsWorker);
	yield takeLatest(updatePpwrEmSettingsRequest.type, updateEmSettingsWorker);
	yield takeLatest(searchPpwrJotformsRequest.type, searchJotformsWorker);
	yield takeLatest(getPpwrJotformRequest.type, getJotformWorker);
	yield takeLatest(activatePpwrServiceRequest.type, activateServiceWorker);
	yield takeLatest(getPpwrFeeOptionsRequest.type, getFeeOptionsWorker);
	yield takeLatest(searchPpwrEmPackagingTypesRequest.type, searchEmPackagingTypesWorker);
	// The edit dialog saves a row and moves on to the next one, so saves must not cancel each other.
	yield takeEvery(updatePpwrEmPackagingTypeRequest.type, updateEmPackagingTypeWorker);
	yield takeEvery(
		uploadPpwrEmPackagingTypeDocumentRequest.type,
		uploadEmPackagingTypeDocumentWorker,
	);
	yield takeEvery(
		downloadPpwrEmPackagingTypeDocumentRequest.type,
		downloadEmPackagingTypeDocumentWorker,
	);
	yield takeEvery(
		deletePpwrEmPackagingTypeDocumentRequest.type,
		deleteEmPackagingTypeDocumentWorker,
	);
	yield takeLatest(searchPpwrEmCountryExclusionsRequest.type, searchEmCountryExclusionsWorker);
	yield takeLatest(createPpwrEmCountryExclusionRequest.type, createEmCountryExclusionWorker);
	yield takeLatest(updatePpwrEmCountryExclusionRequest.type, updateEmCountryExclusionWorker);
}
